import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';
import { submitContactForm } from '../services/firebaseService';
import { FadeInUp, SlideIn } from '../components/Animations';
import { sanitizeText, isValidEmail, isValidPhone, isValidName, isValidMessage, checkRateLimit } from '../utils/validation';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const serviceOptions = t('contactPage.serviceOptions', { returnObjects: true });
  const faqItems = t('contactPage.faqItems', { returnObjects: true });

  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [agreed, setAgreed] = useState(false);
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;

    // Rate limiting: max 3 submissions per minute
    if (!checkRateLimit('contact-form', 3, 60000)) {
      toast.error(t('contactPage.tooManyAttempts') || 'Too many attempts. Please wait a moment.');
      return;
    }

    // Validate inputs
    if (!isValidName(form.name)) {
      toast.error(t('contactPage.invalidName') || 'Please enter a valid name (2-100 characters).');
      return;
    }
    if (!isValidEmail(form.email)) {
      toast.error(t('contactPage.invalidEmail') || 'Please enter a valid email address.');
      return;
    }
    if (!isValidPhone(form.phone)) {
      toast.error(t('contactPage.invalidPhone') || 'Please enter a valid phone number.');
      return;
    }
    if (!isValidMessage(form.message)) {
      toast.error(t('contactPage.invalidMessage') || 'Message must be between 10 and 2000 characters.');
      return;
    }

    setSending(true);
    try {
      await submitContactForm({
        name: sanitizeText(form.name),
        email: sanitizeText(form.email),
        phone: sanitizeText(form.phone),
        service: sanitizeText(form.service),
        message: sanitizeText(form.message),
      });
      toast.success(t('contactPage.successMsg'));
      setForm({ name: '', email: '', phone: '', service: '', message: '' });
      setAgreed(false);
    } catch {
      toast.error(t('contactPage.errorMsg'));
    } finally {
      setSending(false);
    }
  };

  const contactCards = [
    { icon: 'fas fa-phone-alt', title: t('contactPage.phone'), text: t('contactPage.phoneValue') },
    { icon: 'fas fa-envelope', title: t('contactPage.email'), text: t('contactPage.emailValue') },
    { icon: 'fas fa-map-marker-alt', title: t('contactPage.location'), text: t('contactPage.locationValue') },
  ];

  return (
    <>
      <SEO
        title={isRTL ? 'وجود لينك | تواصل معنا' : 'Wojod Link | Contact Us'}
        description={t('contactPage.description')}
      />

      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <FadeInUp>
            <div className="page-hero-content">
              <span className="section-label">{t('contactPage.label')}</span>
              <h1 className="page-title">{t('contactPage.title')}</h1>
              <p className="page-description">{t('contactPage.description')}</p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-page">
        <div className="container">
          <div className="contact-grid">
            {/* Left: Info */}
            <SlideIn direction={isRTL ? 'right' : 'left'}>
              <div>
                <h2 className="section-title" style={{ fontSize: '1.4rem', marginBottom: 24 }}>
                  {t('contactPage.infoTitle')}
                </h2>

                {contactCards.map((card, i) => (
                  <div className="contact-info-card" key={i}>
                    <div className="contact-info-icon">
                      <i className={card.icon} />
                    </div>
                    <div>
                      <div className="contact-info-title">{card.title}</div>
                      <div className="contact-info-text">{card.text}</div>
                    </div>
                  </div>
                ))}

                {/* Social */}
                <div style={{ marginTop: 28 }}>
                  <h3 style={{ color: 'var(--color-white)', fontSize: '0.95rem', marginBottom: 12 }}>
                    {t('contactPage.followUs')}
                  </h3>
                  <div className="social-icons">
                    {[
                      { icon: 'fab fa-facebook-f', href: '#' },
                      { icon: 'fab fa-x-twitter', href: '#' },
                      { icon: 'fab fa-instagram', href: '#' },
                      { icon: 'fab fa-linkedin-in', href: '#' },
                    ].map((s, i) => (
                      <a key={i} href={s.href} className="social-icon" aria-label={s.icon}>
                        <i className={s.icon} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </SlideIn>

            {/* Right: Form */}
            <SlideIn direction={isRTL ? 'left' : 'right'} delay={0.15}>
              <div className="contact-form-wrapper">
                <h2 style={{ color: 'var(--color-white)', fontSize: '1.3rem', marginBottom: 6 }}>
                  {t('contactPage.formTitle')}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', marginBottom: 24 }}>
                  {t('contactPage.formDesc')}
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>{t('contactPage.nameLabel')}</label>
                    <input
                      type="text"
                      name="name"
                      className="form-input"
                      placeholder={t('contactPage.namePlaceholder')}
                      value={form.name}
                      onChange={handleChange}
                      required
                      maxLength={100}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('contactPage.emailLabel')}</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      placeholder={t('contactPage.emailContactPlaceholder')}
                      value={form.email}
                      onChange={handleChange}
                      required
                      maxLength={254}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('contactPage.phoneLabel')}</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      placeholder={t('contactPage.phonePlaceholder')}
                      value={form.phone}
                      onChange={handleChange}
                      maxLength={20}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('contactPage.serviceLabel')}</label>
                    <select
                      name="service"
                      className="form-select"
                      value={form.service}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('contactPage.serviceDefault')}</option>
                      {serviceOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t('contactPage.messageLabel')}</label>
                    <textarea
                      name="message"
                      className="form-textarea"
                      placeholder={t('contactPage.messagePlaceholder')}
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      maxLength={2000}
                    />
                  </div>

                  <div className="form-group">
                    <div className="checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          required
                        />
                        {t('contactPage.agreeTerms')}
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-block"
                    disabled={sending || !agreed}
                    style={{ opacity: sending || !agreed ? 0.6 : 1 }}
                  >
                    {sending ? (
                      <>{t('contactPage.sending')} <i className="fas fa-spinner fa-spin" /></>
                    ) : (
                      <>{t('contactPage.send')} <i className={`fas fa-paper-plane`} /></>
                    )}
                  </button>
                </form>
              </div>
            </SlideIn>
          </div>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="map-section">
        <div className="container">
          <FadeInUp>
            <h2 className="section-title text-center" style={{ marginBottom: 28 }}>
              {t('contactPage.mapTitle')}
            </h2>
            <div className="map-wrapper">
              <i className="fas fa-map-marked-alt" />
              <p>{t('contactPage.locationValue')}</p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <FadeInUp>
            <div className="section-header text-center">
              <span className="section-label">{t('contactPage.faqLabel')}</span>
              <h2 className="section-title">{t('contactPage.faqTitle')}</h2>
            </div>
          </FadeInUp>

          <div className="faq-container">
            {faqItems.map((item, i) => (
              <FadeInUp key={i} delay={i * 0.06}>
                <div className={`faq-item${openFaq === i ? ' active' : ''}`}>
                  <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span>{item.q}</span>
                    <i className="fas fa-chevron-down" />
                  </button>
                  <div className="faq-answer">
                    <p>{item.a}</p>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
